import update from 'immutability-helper';
import { isNumeric } from 'Utils';
import RecipientManagerConstants from 'SelfServePostBuilderSidePanel/RecipientManager/RecipientManagerConstants';
import SelfServeRecipientBuilderEnum from 'SelfServeRecipientBuilder/SelfServeRecipientBuilderConstants';
import PopupConstants from 'Popups/PopupConstants';
import SelfServeBuilderEnum from './SelfServeBuilderConstants';

const initialState = {
  accessLevel: '',
  documentUrl: '',
  documentType: '',
  pages: [],
  // currentPage is zero indexed and manages what page to display
  currentPage: 0,
  // currentPageInterface is the value displayed to user, it is one indexed when scrolling but can
  // be any value if updated by the user
  currentPageInterface: 0,
  // currentPagePagination is one indexed and is used to determine when a page scroll
  // is necessary based on user input. Does similar function to `currentPageInterface`,
  // but only keeps track of valid numbers
  currentPagePagination: 0,
  documentName: 'Custom Document',
  waypointsEnabled: true,
  popupCreationEnabled: true,
  isPublic: false,
  isScalingEnabled: false,
  isShared: false,
  publishedAt: null,
  showPopupCreationError: false,
  isRecipientPreviewModalVisible: false,
  isLoading: true,
  fieldOutline: {},
  isNotificationError: false,
  isShareFinished: false,
  isSampleState: false,
  isFirstSamplePopupMounted: false,
  isZeroStateModalOpen: false,
  notification: '',
  highlightedFieldId: '',
  isfirstPopupCreated: false,
};

const SelfServeBuilderReducer = (state, action) => {
  if (typeof state === 'undefined') {
    return initialState;
  }
  switch (action.type) {
    case SelfServeBuilderEnum.SELF_SERVE_BUILDER_WAYPOINTS_DISABLE:
      return update(state, {
        waypointsEnabled: { $set: false },
      });
    case SelfServeBuilderEnum.SELF_SERVE_BUILDER_WAYPOINTS_ENABLE:
      return update(state, {
        waypointsEnabled: { $set: true },
      });
    case SelfServeBuilderEnum.SELF_SERVE_BUILDER_SET_PAGES_FROM_FILE:
      return update(state, {
        pages: { $set: action.pages },
        currentPageInterface: { $set: 1 },
        currentPage: { $set: 0 },
      });
    case SelfServeBuilderEnum.SELF_SERVE_BUILDER_CURRENT_PAGE_SET: {
      return update(state, {
        currentPageInterface: { $set: action.currentPage + 1 },
        currentPage: { $set: action.currentPage },
      });
    }
    case SelfServeBuilderEnum.SELF_SERVE_BUILDER_PAGINATION_CURRENT_PAGE_SET: {
      let updatedCurrentPage = state.currentPage;
      if (isNumeric(action.currentPage)) {
        const parsedCurrentPage = parseInt(action.currentPage, 10);
        if (parsedCurrentPage > 0 && parsedCurrentPage <= state.pages.length) {
          updatedCurrentPage = parsedCurrentPage - 1;
        }
      }
      return update(state, {
        currentPagePagination: { $set: action.currentPage },
        currentPageInterface: { $set: action.currentPage },
        currentPage: { $set: updatedCurrentPage },
      });
    }
    case SelfServeBuilderEnum.SELF_SERVE_BUILDER_PAGINATION_BACK:
      if (state.currentPage <= 0) {
        return state;
      }
      return update(state, {
        currentPage: { $set: state.currentPage - 1 },
        currentPageInterface: { $set: state.currentPage },
        currentPagePagination: { $set: state.currentPage },
      });
    case SelfServeBuilderEnum.SELF_SERVE_BUILDER_PAGINATION_NEXT:
      if (state.currentPage >= state.pages.length - 1) {
        return state;
      }
      return update(state, {
        currentPage: { $set: state.currentPage + 1 },
        currentPageInterface: { $set: state.currentPage + 2 },
        currentPagePagination: { $set: state.currentPage + 2 },
      });
    case SelfServeBuilderEnum.SELF_SERVE_BUILDER_POPUP_CREATION_ENABLED_SET: {
      const popupCreationEnabled = action.enabled !== undefined ?
        action.enabled :
        state.popupCreationEnabled;
      return update(state, {
        popupCreationEnabled: { $set: popupCreationEnabled },
        showPopupCreationError: { $set: false },
      });
    }
    case SelfServeBuilderEnum.SELF_SERVE_BUILDER_POPUP_CREATION_ATTEMPT_SET:
      return update(state, {
        showPopupCreationError: { $set: true },
      });

    case SelfServeBuilderEnum.SELF_SERVE_BUILDER_CUSTOM_TEMPLATE_DATA_GET_REQUEST:
      return update(state, {
        documentType: { $set: action.documentType },
      });
    case SelfServeBuilderEnum.SELF_SERVE_BUILDER_CUSTOM_TEMPLATE_DATA_GET_SUCCESS:
      return update(state, {
        popupCreationEnabled: { $set: !action.data.custom_template.field_outline.activePopup },
        isPublic: { $set: action.data.custom_template.public },
        publishedAt: { $set: action.data.custom_template.published_at },
        documentName: { $set: action.data.custom_template.name },
      });
    case SelfServeBuilderEnum.SELF_SERVE_BUILDER_SCALING_MODE_SET:
      return update(state, {
        isScalingEnabled: { $set: action.isScalingEnabled },
      });
    case SelfServeBuilderEnum.SELF_SERVE_BUILDER_RECIPIENT_PREVIEW_MODAL_SET:
      return update(state, {
        isRecipientPreviewModalVisible: { $set: action.isRecipientPreviewModalVisible },
      });
    case SelfServeBuilderEnum.SELF_SERVE_BUILDER_LOADING_START:
      return update(state, {
        isLoading: { $set: action.loading },
      });
    case SelfServeBuilderEnum.SELF_SERVE_BUILDER_LOADING_DONE:
      return update(state, {
        isLoading: { $set: action.loading },
      });
    case SelfServeBuilderEnum.SELF_SERVE_BUILDER_NOTIFICATION_SET:
      return update(state, {
        notification: { $set: action.notification },
        isNotificationError: { $set: action.isError },
      });
    case RecipientManagerConstants.RECIPIENT_MANAGER_PUBLIC_LINK_UPDATE:
      return update(state, {
        isPublic: { $set: action.data.custom_template.public },
        publishedAt: { $set: action.data.custom_template.published_at },
      });
    case RecipientManagerConstants.RECIPIENT_MANAGER_CUSTOM_TEMPLATE_SHARE_SUCCESS:
      return update(state, {
        isShared: { $set: true },
        publishedAt: { $set: action.publishedAt },
      });
    case SelfServeRecipientBuilderEnum.SELF_SERVE_RECIPIENT_BUILDER_SET_FINISHED:
      return update(state, {
        isShareFinished: { $set: true },
        accessLevel: { $set: action.accessLevel || '' },
      });
    case SelfServeBuilderEnum.SELF_SERVE_BUILDER_ZERO_STATE_SET: {
      return update(state, {
        isZeroStateModalOpen: { $set: true },
      });
    }
    case SelfServeBuilderEnum.SELF_SERVE_BUILDER_SAMPLE_STATE_SET: {
      return update(state, {
        isSampleState: { $set: true },
      });
    }
    case SelfServeBuilderEnum.SELF_SERVE_BUILDER_SAMPLE_STATE_FIRST_POPUP_MOUNTED: {
      return update(state, {
        isFirstSamplePopupMounted: { $set: true },
      });
    }
    case SelfServeRecipientBuilderEnum.SELF_SERVE_RECIPIENT_BUILDER_HIGHLIGHT_FIELD: {
      return update(state, {
        highlightedFieldId: { $set: action.fieldId },
      });
    }
    case SelfServeBuilderEnum.SELF_SERVE_BUILDER_FIRST_POPUP_CREATED:
      return update(state, {
        isfirstPopupCreated: { $set: true },
      });
    case PopupConstants.EDIT_POPUP_END:
      if (!state.isfirstPopupCreated) {
        return update(state, {
          isfirstPopupCreated: { $set: true },
          isRecipientPreviewModalVisible: { $set: true },
        });
      }
      return state;
    default:
      return state;
  }
};

export default SelfServeBuilderReducer;



// WEBPACK FOOTER //
// ./src-react/SelfServeBuilder/SelfServeBuilderReducer.jsx